import {RequestQueryBuilder, SCondition} from '@nestjsx/crud-request';
import {ComponentProjectUpdate} from '../models';
import {MsalService} from '@azure/msal-angular';
import {ComponentProjectChecklists} from '../models';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Burly} from 'kb-burly';
import {ManyResponse} from '../../../responses';
import {ComponentProject} from '../models';

@Injectable({
  providedIn: 'root'
})
export class TorchProjectsService {

  constructor(protected config: TorchAPIConfiguration,
              private httpClient: HttpClient,
              protected msalService: MsalService) {
  }

  getProjectsByCPN(companyPartNumber: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addQuery('filter', 'companyPartNumber||$eq||' + companyPartNumber)
      .get;

    return this.httpClient.get<ManyResponse<ComponentProject>>(endpoint);
  }

  getProjectCountByCPN(companyPartNumber: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/count')
      .addSegment('/cpn/' + companyPartNumber)
      .get;

    return this.httpClient.get<number>(endpoint);
  }

  getComponentProjectById(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addQuery('filter', 'id||$eq||' + projectId)
      .get;

    return this.httpClient.get<ManyResponse<ComponentProject>>(endpoint).pipe(
      map(response => response.data)
    );
  }

  getChecklistsByProjectId(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/component-project-checklists')
      .addSegment('/project/' + projectId)
      .get;

    return this.httpClient.get<ComponentProjectChecklists>(endpoint).pipe(
      map(response => response)
    );
  }

  public queryProjects(pageSize: number = 15,
                       currentPage: number = 0,
                       sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
                       sortBy: string = 'id',
                       filters: SCondition | null): Observable<ManyResponse<ComponentProject>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<ComponentProject>>(endpoint);
  }

  public getATAGroups(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/ataGroups')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ataGroup: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ataGroup))
    );
  }

  public getATAChapters(contains): Observable<number[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/ataChapters')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ataChapter: number }]>(endpoint).pipe(
      map(response => response.map(v => v.ataChapter))
    );
  }

  public getTeamMembers(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/teamMembers')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ respParty: string }]>(endpoint).pipe(
      map(response => response.map(v => v.respParty))
    );
  }

  public getProjectFleets(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/projectFleets')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ majorFleet: string }]>(endpoint).pipe(
      map(response => response.map(v => v.majorFleet))
    );
  }

  public getProjectCategories(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/projectCategories')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ mainType: string }]>(endpoint).pipe(
      map(response => response.map(v => v.mainType))
    );
  }

  createChecklist(list: ComponentProjectChecklists) {
    list.createdBy = this.msalService.instance.getActiveAccount().username;

    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/component-project-checklists')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, list)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  deleteChecklist(list: ComponentProjectChecklists) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/component-project-checklists')
      .addSegment('/' + list.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  deleteChecklistItem(checklistItemId: number) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/component-project-checklists')
      .addSegment('/item')
      .addSegment('/' + checklistItemId)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  updateChecklist(checklist: ComponentProjectChecklists) {
    checklist.updated = new Date();

    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/component-project-checklists')
      .addSegment('/' + checklist.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.patch(endpointURL, checklist)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  addProject(project: ComponentProject) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, project)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  updateProject(project: ComponentProject) {
    project.updatedAt = new Date();
    project.updatedBy = this.msalService.instance.getActiveAccount().username;

    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/' + project.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.patch(endpointURL, project)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public deleteProject(project: ComponentProject) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/projects')
      .addSegment('/' + project.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  getProjectUpdates(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/project-updates')
      .addQuery('filter', 'projectId||$eq||' + projectId)
      .get;

    return this.httpClient.get<ManyResponse<ComponentProjectUpdate>>(endpoint).pipe(
      map(response => response.data)
    );
  }

  public addProjectUpdate(update: ComponentProjectUpdate) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/project-updates')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, update)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public deleteProjectUpdate(update: ComponentProjectUpdate) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/project-updates')
      .addSegment('/' + update.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }
}
